"use client";

import { useState } from "react";
import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HubbleResults } from "@/components/tools/astronomy/hubble-results";
import { QuantityField } from "@/components/tools/physics-fields/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  H0_CHOICES,
  type H0Choice,
  HUBBLE_DISTANCE_UNITS,
  HUBBLE_VELOCITY_UNITS,
} from "@/lib/tool-kit/astronomy/hubble";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";

/** English strings. A translated content file overrides these through `ui`. */
export const HUBBLE_LAW_CALCULATOR_STRINGS = {
  mode: "Calculate",
  modes: {
    velocity: "Recession velocity from distance",
    distance: "Distance from recession velocity",
    redshift: "Velocity and distance from redshift",
  },
  h0: "Hubble constant H₀",
  h0Names: {
    planck: "Planck 2018 (cosmic microwave background)",
    shoes: "SH0ES 2022 (Cepheids and supernovae)",
    cchp: "CCHP 2025 (tip of the red giant branch)",
    round: "Round textbook value",
    custom: "Your own value",
  },
  h0Option: "{name}: {value}",
  customH0: "H₀ in km/s per Mpc",
  distanceLabel: "Distance",
  distanceUnit: "Unit for the distance",
  velocityLabel: "Recession velocity",
  velocityUnit: "Unit for the velocity",
  redshiftLabel: "Redshift z",
  example: "Start from an example",
  custom: "Custom value",
  exampleNames: {
    coma: "Coma Cluster (z = 0.0231)",
    quasar: "Quasar 3C 273 (z = 0.158)",
    gnz11: "Galaxy GN-z11 (z = 10.6)",
  },
  missing: "Enter a positive number. Scientific notation such as 1.5e4 works.",
  badH0: "Enter a Hubble constant greater than zero.",
  results: "Results",
  velocity: "Recession velocity",
  velocityCz: "Velocity, low-redshift c·z",
  velocityRel: "Velocity, relativistic Doppler",
  kms: "{value} km/s",
  fractionOfC: "Fraction of the speed of light",
  fractionValue: "{value} c",
  distance: "Distance",
  distanceMpc: "Distance in megaparsecs",
  distanceGly: "Distance in light-years",
  lightYears: "{value} light-years",
  rangeHeading: "Range from the uncertainty in H₀",
  rangeLine: "With H₀ = {h0} ± {sigma}, the result runs from {low} to {high}.",
  hubbleTime: "Hubble time 1/H₀",
  hubbleTimeValue: "{value} billion years",
  hubbleDistance: "Hubble distance c/H₀",
  hubbleDistanceValue: "{value} Mpc",
  mpcValue: "{value} Mpc",
  fasterThanLight:
    "This recession velocity is above the speed of light. That does not break relativity: it is space itself that is expanding, and nothing moves through space faster than light. Objects beyond the Hubble distance recede faster than c but their light can still reach us.",
  lowRedshiftWarning:
    "Hubble's law is a low-redshift approximation, good to a few percent only for z up to about 0.1. Beyond that the distance depends on the full expansion history, and the relativistic Doppler formula is not the right measure of a cosmological redshift either.",
  lcdmLine:
    "For comparison, in a flat ΛCDM universe with Ωₘ = 0.315 the comoving distance to z = {z} is {mpc} Mpc, which is {percent}% {direction} the simple Hubble-law distance.",
  smaller: "below",
  larger: "above",
  compareHeading: "The same input with each published H₀",
  sourceColumn: "Hubble constant",
  valueColumn: "H₀ (km/s/Mpc)",
  resultColumn: "Result",
  timeColumn: "Hubble time (Gyr)",
  plusMinus: "{value} ± {sigma}",
  copy: "Copy result",
  copied: "Copied!",
  summary: "H₀ = {h0} km/s/Mpc: {input} gives {output}.",
  note: "H₀ values: Planck Collaboration 2020 (A&A 641, A6), Riess et al. 2022 (ApJ 934, L7) and Freedman et al. 2025 (ApJ 985, 203, with its three errors added in quadrature). They disagree by more than their stated errors, which is the Hubble tension; nobody yet knows whether the cause is a measurement problem or new physics.",
} as const;

type Strings = typeof HUBBLE_LAW_CALCULATOR_STRINGS;
export type HubbleMode = "velocity" | "distance" | "redshift";
type H0Pick = H0Choice["id"] | "custom";

interface Example {
  z: string;
}

const EXAMPLES: Record<string, Example> = {
  coma: { z: "0.0231" },
  quasar: { z: "0.158" },
  gnz11: { z: "10.6" },
};

export default function HubbleLawCalculatorWidget() {
  const strings: Strings = useToolUi(HUBBLE_LAW_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<HubbleMode>("velocity");
  const [pick, setPick] = useState<H0Pick>("planck");
  const [customH0, setCustomH0] = useState("70");
  const [dText, setDText] = useState("100");
  const [dUnit, setDUnit] = useState("mpc");
  const [vText, setVText] = useState("7000");
  const [vUnit, setVUnit] = useState("kms");
  const [zText, setZText] = useState("0.0231");
  const [example, setExample] = useState("coma");

  const names = strings.h0Names as Record<string, string>;
  const choice = H0_CHOICES.find((c) => c.id === pick);
  const h0 = choice ? choice.value : parseNumberInput(customH0);
  const sigma = choice ? choice.sigma : 0;
  const input =
    mode === "velocity"
      ? readQuantity(dText, HUBBLE_DISTANCE_UNITS, dUnit)
      : readQuantity(vText, HUBBLE_VELOCITY_UNITS, vUnit);
  const z = parseNumberInput(zText);

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.mode}
        name="hl-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as HubbleMode[]).map((id) => ({
          value: id,
          label: strings.modes[id],
        }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="hl-h0"
          label={strings.h0}
          onChange={setPick}
          options={[
            ...H0_CHOICES.map((c) => ({
              value: c.id,
              label: t(strings.h0Option, {
                name: names[c.id] ?? c.id,
                value: fmt(c.value, { maximumFractionDigits: 2 }),
              }),
            })),
            { value: "custom" as H0Pick, label: names.custom },
          ]}
          value={pick}
        />
        {pick === "custom" ? (
          <TextField
            id="hl-custom"
            label={strings.customH0}
            numeric
            onChange={setCustomH0}
            value={customH0}
          />
        ) : (
          <div />
        )}
        {mode === "velocity" ? (
          <QuantityField
            id="hl-distance"
            label={strings.distanceLabel}
            onChange={setDText}
            onUnit={setDUnit}
            unit={dUnit}
            unitLabel={strings.distanceUnit}
            units={HUBBLE_DISTANCE_UNITS}
            value={dText}
          />
        ) : null}
        {mode === "distance" ? (
          <QuantityField
            id="hl-velocity"
            label={strings.velocityLabel}
            onChange={setVText}
            onUnit={setVUnit}
            unit={vUnit}
            unitLabel={strings.velocityUnit}
            units={HUBBLE_VELOCITY_UNITS}
            value={vText}
          />
        ) : null}
        {mode === "redshift" ? (
          <>
            <SelectField
              id="hl-example"
              label={strings.example}
              onChange={(id) => {
                setExample(id);
                const ex = EXAMPLES[id];
                if (ex) {
                  setZText(ex.z);
                }
              }}
              options={[
                { value: "custom", label: strings.custom },
                ...Object.keys(EXAMPLES).map((id) => ({
                  value: id,
                  label:
                    (strings.exampleNames as Record<string, string>)[id] ?? id,
                })),
              ]}
              value={example}
            />
            <TextField
              id="hl-z"
              label={strings.redshiftLabel}
              numeric
              onChange={(next) => {
                setExample("custom");
                setZText(next);
              }}
              value={zText}
            />
          </>
        ) : null}
      </div>
      <HubbleResults
        h0={h0}
        input={mode === "redshift" ? z : input}
        mode={mode}
        sigma={sigma}
        strings={strings}
      />
    </div>
  );
}
